import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Check, X, Plus, Trash2, CheckCircle2, XCircle, Zap } from 'lucide-react';
import { api } from '../api/client';
import { LoadingState, Badge, Btn, Table, Th, Td, TrEmpty } from '../components/ui';

interface EntityFlag {
  flagName: string;
  label: string;
  description: string;
  category: string;
  enabled: boolean;
}

interface SeedResult {
  seeded?: boolean;
  cleared?: boolean;
  counts?: Record<string, number>;
  deleted?: Record<string, number>;
}

const CATEGORY_LABELS: Record<string, string> = {
  special: 'Special',
  tier: 'Tier',
  experiment: 'Experiment',
};

function DemoDataControls({ entityId }: { entityId: string }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<SeedResult | null>(null);

  const seed = useMutation({
    mutationFn: (): Promise<{ seeded: boolean; counts: Record<string, number> }> =>
      api.post(`/entities/${entityId}/seed-demo`),
    onSuccess: (data) => {
      setResult(data);
      void qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] });
    },
  });

  const clear = useMutation({
    mutationFn: (): Promise<{ cleared: boolean; deleted: Record<string, number> }> =>
      api.delete(`/entities/${entityId}/seed-demo`),
    onSuccess: (data) => {
      setResult(data);
      void qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] });
    },
  });

  const isPending = seed.isPending || clear.isPending;

  return (
    <div className="mt-3 border-t border-white/[0.06] pt-3">
      <p className="text-xs text-gray-500 mb-2">
        Populate this entity with a realistic demo dataset (Foundry Commerce), or clear all seed data.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <Btn
          variant="primary"
          disabled={isPending}
          onClick={() => {
            setResult(null);
            seed.mutate();
          }}
        >
          {seed.isPending ? 'Seeding…' : 'Seed Demo Data'}
        </Btn>
        <Btn
          variant="danger"
          disabled={isPending}
          onClick={() => {
            if (confirm('Clear all demo seed data from this entity? This cannot be undone.')) {
              setResult(null);
              clear.mutate();
            }
          }}
        >
          {clear.isPending ? 'Clearing…' : 'Clear Demo Data'}
        </Btn>
      </div>

      {(seed.isError || clear.isError) && (
        <p className="text-xs text-red-400 mt-2">
          {((seed.error ?? clear.error) as Error).message}
        </p>
      )}

      {result && (
        <div className="mt-2 text-xs text-gray-400 font-mono bg-white/[0.03] rounded-lg p-2 border border-white/[0.06]">
          {result.seeded && result.counts && (
            <>
              <span className="text-green-400">Seeded:</span>{' '}
              {Object.entries(result.counts).map(([k, v]) => `${v} ${k}`).join(', ')}
            </>
          )}
          {result.cleared && result.deleted && (
            <>
              <span className="text-yellow-400">Cleared:</span>{' '}
              {Object.entries(result.deleted).map(([k, v]) => `${v} ${k}`).join(', ')}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  slack_message: 'Slack Message', slack_dm: 'Slack DM', create_doc: 'Create Doc',
  create_slides: 'Create Slides', create_calendar_event: 'Calendar Event',
  post_ticket_comment: 'Ticket Comment', update_ticket_status: 'Update Status',
  status_report: 'Status Report', open_url: 'Open URL',
};

function ActionStatsSection({ entityId }: { entityId: string }) {
  const { data, isLoading } = useQuery<{
    total: number;
    byType: Record<string, { total: number; succeeded: number; failed: number }>;
    recentFailures: Array<{ actionType: string; projectId: string | null; message: string | null; timestamp: string | null }>;
  }>({
    queryKey: ['admin', 'entity-action-stats', entityId],
    queryFn: () => api.get(`/entities/${entityId}/action-stats`),
    staleTime: 60_000,
  });

  if (isLoading) return null;
  if (!data || data.total === 0) return (
    <section className="mt-4">
      <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Zap className="h-3.5 w-3.5" /> Action Execution Stats
      </h2>
      <p className="text-xs text-gray-500">No action executions recorded yet.</p>
    </section>
  );

  const totalSucceeded = Object.values(data.byType).reduce((s, v) => s + v.succeeded, 0);
  const successRate = data.total > 0 ? Math.round((totalSucceeded / data.total) * 100) : null;

  return (
    <section className="mt-4">
      <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Zap className="h-3.5 w-3.5" /> Action Execution Stats
      </h2>
      <div className="bg-bg-surface rounded-xl border border-white/[0.06] p-4 space-y-3">
        <div className="flex items-center gap-6 text-sm">
          <span className="text-gray-400">Total: <span className="text-white font-medium">{data.total}</span></span>
          <span className="text-gray-400">Success rate: <span className="text-emerald-400 font-medium">{successRate !== null ? `${successRate}%` : '—'}</span></span>
          <span className="text-gray-400">Fallbacks: <span className="text-red-400 font-medium">{data.total - totalSucceeded}</span></span>
        </div>
        <div className="space-y-1.5">
          {Object.entries(data.byType).sort(([,a],[,b]) => b.total - a.total).map(([type, stat]) => {
            const rate = stat.total > 0 ? Math.round((stat.succeeded / stat.total) * 100) : 0;
            return (
              <div key={type} className="flex items-center gap-2 text-xs">
                <span className="w-36 text-gray-300 shrink-0">{ACTION_LABELS[type] ?? type}</span>
                <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${rate}%` }} />
                </div>
                <span className="w-8 text-right text-gray-400">{rate}%</span>
                <span className="flex items-center gap-0.5 text-emerald-400 w-8"><CheckCircle2 className="h-3 w-3" />{stat.succeeded}</span>
                <span className="flex items-center gap-0.5 text-red-400 w-8"><XCircle className="h-3 w-3" />{stat.failed}</span>
              </div>
            );
          })}
        </div>
        {data.recentFailures.length > 0 && (
          <div className="border-t border-white/[0.06] pt-3 space-y-1.5">
            <p className="text-xs font-medium text-gray-400">Recent fallbacks</p>
            {data.recentFailures.slice(0, 10).map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-gray-400">
                <XCircle className="h-3 w-3 text-red-400 mt-0.5 shrink-0" />
                <span className="text-red-300 shrink-0">{ACTION_LABELS[f.actionType] ?? f.actionType}</span>
                {f.message && <span className="truncate">{f.message}</span>}
                {f.timestamp && <span className="shrink-0 ml-auto">{new Date(f.timestamp).toLocaleDateString()}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function EntityFlagsSection({ entityId }: { entityId: string }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<{ flags: EntityFlag[] }>({
    queryKey: ['admin', 'entity-flags', entityId],
    queryFn: () => api.get(`/entities/${entityId}/flags`),
  });

  const toggle = useMutation({
    mutationFn: ({ flagName, enabled }: { flagName: string; enabled: boolean }) =>
      api.put(`/entities/${entityId}/flags/${flagName}`, { enabled }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'entity-flags', entityId] }),
  });

  const flags = data?.flags ?? [];
  const demoFlag = flags.find(f => f.flagName === 'demo_entity');

  return (
    <section className="mt-4">
      <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Feature Flags</h2>
      <div className="bg-bg-surface rounded-xl border border-white/[0.06] divide-y divide-white/[0.04]">
        {isLoading && (
          <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>
        )}
        {!isLoading && flags.length === 0 && (
          <div className="px-4 py-3 text-sm text-gray-500">No flags defined.</div>
        )}
        {flags.map(f => (
          <div key={f.flagName} className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">{f.label}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-gray-400 font-mono">
                    {f.flagName}
                  </span>
                  {f.category && CATEGORY_LABELS[f.category] && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary/80">
                      {CATEGORY_LABELS[f.category]}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.description}</p>
              </div>
              <button
                role="switch"
                aria-checked={f.enabled}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ flagName: f.flagName, enabled: !f.enabled })}
                className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                  f.enabled ? 'bg-primary' : 'bg-white/10'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    f.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {f.flagName === 'demo_entity' && demoFlag?.enabled && (
              <DemoDataControls entityId={entityId} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

const ENTITY_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

interface EntityUser {
  userId: string;
  email: string;
  fullName: string;
  role: string;
  entityRole?: string;
}

function AddMemberModal({ entityId, onClose }: { entityId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [successMsg, setSuccessMsg] = useState('');

  const add = useMutation({
    mutationFn: (): Promise<{ invited: boolean }> =>
      api.post(`/entities/${entityId}/members`, { email: email.trim(), entityRole: role, functionalRoles: [] }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users', entityId] });
      setSuccessMsg(
        data.invited
          ? `Account created. A password-setup email has been sent to ${email.trim()}.`
          : `${email.trim()} has been added to this entity.`,
      );
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-bg-surface border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Add Member</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {successMsg ? (
          <div className="py-4">
            <p className="text-sm text-green-400 leading-relaxed">{successMsg}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Email</label>
              <input
                autoFocus
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50"
                onKeyDown={e => { if (e.key === 'Enter' && email.trim()) add.mutate(); }}
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Role</label>
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                className="w-full bg-bg-surface border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/50"
              >
                {ENTITY_ROLES.map(r => (
                  <option key={r.value} value={r.value} className="bg-bg-surface text-white">{r.label}</option>
                ))}
              </select>
            </div>

            {add.isError && (
              <p className="text-sm text-red-400">{(add.error as Error).message}</p>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-xl text-sm text-gray-400 bg-white/5 hover:bg-white/10 transition-colors"
          >
            {successMsg ? 'Close' : 'Cancel'}
          </button>
          {!successMsg && (
            <button
              onClick={() => add.mutate()}
              disabled={!email.trim() || add.isPending}
              className="flex-1 py-2 rounded-xl text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {add.isPending ? 'Adding...' : 'Add Member'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface Entity {
  id: string;
  name: string;
  logoUrl?: string | null;
  suspendedAt: string | null;
  deletedAt: string | null;
  createdAt?: string;
}

export function EntityDetail() {
  const { entityId } = useParams<{ entityId: string }>();
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLogo, setEditLogo] = useState('');
  const [editingSub, setEditingSub] = useState(false);
  const [subDraft, setSubDraft] = useState<{ plan: string; seatLimit: string; stripeCustomerId: string }>({ plan: '', seatLimit: '', stripeCustomerId: '' });

  const { data: entity, isLoading } = useQuery<Entity>({
    queryKey: ['admin', 'entities', entityId],
    queryFn: () => api.get(`/entities/${entityId}`),
    enabled: !!entityId,
  });

  const { data: sub } = useQuery({
    queryKey: ['admin', 'subscriptions', entityId],
    queryFn: () => api.get(`/subscriptions/${entityId}`),
    enabled: !!entityId,
  });

  const update = useMutation({
    mutationFn: (body: { name?: string; logoUrl?: string }) =>
      api.put(`/entities/${entityId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'entities'] });
      setEditing(false);
    },
  });

  const suspend = useMutation({
    mutationFn: () => api.post(`/entities/${entityId}/suspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] }),
  });

  const unsuspend = useMutation({
    mutationFn: () => api.post(`/entities/${entityId}/unsuspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] }),
  });

  const softDelete = useMutation({
    mutationFn: () => api.delete(`/entities/${entityId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'entities', entityId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'entities'] });
    },
  });

  const updateSub = useMutation({
    mutationFn: (body: { plan?: string; seatLimit?: number | null; stripeCustomerId?: string }) =>
      api.put(`/subscriptions/${entityId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'subscriptions', entityId] });
      setEditingSub(false);
    },
  });

  const { data: usersData } = useQuery<{ users: EntityUser[] }>({
    queryKey: ['admin', 'users', entityId],
    queryFn: () => api.get(`/users/search?entity_id=${entityId}`),
    enabled: !!entityId,
  });

  const changeRole = useMutation({
    mutationFn: ({ userId, entityRole }: { userId: string; entityRole: string }) =>
      api.put(`/users/${entityId}/${userId}/role`, { entityRole }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'users', entityId] }),
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.delete(`/entities/${entityId}/members/${userId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'users', entityId] }),
  });

  if (isLoading) return <LoadingState />;
  if (!entity) return <p className="text-sm text-gray-400 py-8">Entity not found</p>;

  const isSuspended = !!entity.suspendedAt;
  const isDeleted = !!entity.deletedAt;
  const pending = suspend.isPending || unsuspend.isPending || softDelete.isPending || update.isPending;

  const startEdit = () => {
    setEditName(entity.name);
    setEditLogo(entity.logoUrl ?? '');
    setEditing(true);
  };

  const s = sub as Record<string, unknown> | undefined;

  return (
    <div>
      <Link
        to="/entities"
        className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Entities
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                autoFocus
                value={editName}
                onChange={e => setEditName(e.target.value)}
                className="flex-1 bg-bg-surface border border-white/10 rounded-lg px-3 py-1.5 text-white text-base font-semibold focus:outline-none focus:border-primary/50"
              />
              <button
                onClick={() => update.mutate({ name: editName, logoUrl: editLogo || undefined })}
                disabled={!editName.trim() || pending}
                className="p-1.5 text-green-400 hover:text-green-300 disabled:opacity-40 transition-colors"
                title="Save"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={() => setEditing(false)}
                className="p-1.5 text-gray-400 hover:text-white transition-colors"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-white truncate">{entity.name}</h1>
              {isDeleted ? (
                <Badge variant="danger">Deleted</Badge>
              ) : isSuspended ? (
                <Badge variant="warning">Suspended</Badge>
              ) : (
                <Badge variant="success">Active</Badge>
              )}
              {!isDeleted && (
                <button
                  onClick={startEdit}
                  className="text-gray-500 hover:text-white transition-colors flex-shrink-0"
                  title="Edit entity"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </>
          )}
        </div>

        {/* Actions */}
        {!isDeleted && !editing && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {isSuspended ? (
              <Btn variant="ghost" disabled={pending} onClick={() => unsuspend.mutate()}>
                Unsuspend
              </Btn>
            ) : (
              <Btn variant="danger" disabled={pending} onClick={() => suspend.mutate()}>
                Suspend
              </Btn>
            )}
            <Btn
              variant="danger"
              disabled={pending}
              onClick={() => {
                if (confirm(`Delete "${entity.name}"? This cannot be undone.`)) {
                  softDelete.mutate();
                }
              }}
            >
              Delete
            </Btn>
          </div>
        )}
      </div>

      <p className="font-mono text-xs text-gray-500 mb-4">{entity.id}</p>

      {/* Logo URL edit (shown when editing) */}
      {editing && (
        <div className="mb-4">
          <label className="block text-xs text-gray-400 mb-1">Logo URL (optional)</label>
          <input
            value={editLogo}
            onChange={e => setEditLogo(e.target.value)}
            placeholder="https://..."
            className="w-full bg-bg-surface border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50"
          />
        </div>
      )}

      {update.isError && (
        <p className="text-sm text-red-400 mb-4">{(update.error as Error).message}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section>
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Entity</h2>
          <div className="bg-bg-surface rounded-xl border border-white/[0.06] p-4">
            <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap overflow-auto">
              {JSON.stringify(entity, null, 2)}
            </pre>
          </div>
        </section>

      </div>

      {s && (
        <section className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider">Subscription</h2>
            {!editingSub && (
              <button
                onClick={() => {
                  setSubDraft({
                    plan: (s?.plan as string) ?? '',
                    seatLimit: s?.seatLimit != null ? String(s.seatLimit as number) : '',
                    stripeCustomerId: (s?.stripeCustomerId as string) ?? '',
                  });
                  setEditingSub(true);
                }}
                className="text-gray-500 hover:text-white transition-colors"
                title="Edit subscription"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="bg-bg-surface rounded-xl border border-white/[0.06] p-4">
            {editingSub ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Plan</label>
                  <input
                    type="text"
                    value={subDraft.plan}
                    onChange={e => setSubDraft(d => ({ ...d, plan: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Seat Limit</label>
                  <input
                    type="number"
                    value={subDraft.seatLimit}
                    onChange={e => setSubDraft(d => ({ ...d, seatLimit: e.target.value }))}
                    placeholder="Leave empty for unlimited"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Stripe Customer ID</label>
                  <input
                    type="text"
                    value={subDraft.stripeCustomerId}
                    onChange={e => setSubDraft(d => ({ ...d, stripeCustomerId: e.target.value }))}
                    placeholder="cus_... (optional)"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50"
                  />
                </div>
                {updateSub.isError && (
                  <p className="text-sm text-red-400">{(updateSub.error as Error).message}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <Btn
                    variant="primary"
                    disabled={updateSub.isPending}
                    onClick={() => {
                      const body: { plan?: string; seatLimit?: number | null; stripeCustomerId?: string } = {};
                      if (subDraft.plan !== '') body.plan = subDraft.plan;
                      if (subDraft.seatLimit !== '') body.seatLimit = parseInt(subDraft.seatLimit, 10);
                      else body.seatLimit = null;
                      if (subDraft.stripeCustomerId !== '') body.stripeCustomerId = subDraft.stripeCustomerId;
                      updateSub.mutate(body);
                    }}
                  >
                    {updateSub.isPending ? 'Saving...' : 'Save'}
                  </Btn>
                  <Btn variant="ghost" onClick={() => setEditingSub(false)}>
                    Cancel
                  </Btn>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Plan</p>
                  <p className="text-sm text-white font-medium">{(s?.plan as string) || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Seat Limit</p>
                  <p className="text-sm text-white">
                    {s?.seatLimit != null ? String(s.seatLimit as number) : 'Unlimited'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Stripe Customer ID</p>
                  <p className="text-sm text-gray-300 font-mono">
                    {(s?.stripeCustomerId as string) || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Status</p>
                  {s?.suspendedAt ? (
                    <Badge variant="warning">Suspended</Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Members */}
      <section className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider">Members</h2>
          <Btn variant="ghost" onClick={() => setShowAddMember(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add Member
          </Btn>
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Name / Email</Th>
              <Th>Role</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {!usersData?.users?.length ? (
              <TrEmpty cols={3} message="No members found" />
            ) : (
              usersData.users.map(u => (
                <tr key={u.userId} className="hover:bg-white/[0.02] transition-colors">
                  <Td>
                    <p className="text-gray-200">{u.fullName || u.email}</p>
                    {u.fullName && <p className="text-xs text-gray-500">{u.email}</p>}
                  </Td>
                  <Td>
                    <select
                      value={u.entityRole ?? (u.role === 'admin' ? 'admin' : 'member')}
                      onChange={e => changeRole.mutate({ userId: u.userId, entityRole: e.target.value })}
                      className="bg-bg-surface border border-white/10 rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-primary/50"
                    >
                      {ENTITY_ROLES.map(r => (
                        <option key={r.value} value={r.value} className="bg-bg-surface text-white">{r.label}</option>
                      ))}
                    </select>
                  </Td>
                  <Td>
                    <button
                      disabled={removeMember.isPending}
                      onClick={() => {
                        if (confirm(`Remove ${u.fullName || u.email} from this entity?`)) {
                          removeMember.mutate(u.userId);
                        }
                      }}
                      className="text-gray-600 hover:text-red-400 transition-colors disabled:opacity-40"
                      title="Remove member"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </section>

      {entityId && <EntityFlagsSection entityId={entityId} />}
      {entityId && <ActionStatsSection entityId={entityId} />}

      {showAddMember && entityId && (
        <AddMemberModal entityId={entityId} onClose={() => setShowAddMember(false)} />
      )}
    </div>
  );
}
